'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { ArrowRightIcon, CodeIcon, SparkleIcon } from '@phosphor-icons/react';
import { Box, Button, Card, CardContent, Divider, Grid2 as Grid, Stack, Typography } from '@unity/cloud-ui';
import { styled } from '@unity/cloud-ui/styles';
import { ThemeContext } from '@/components/theme';
import themeConstants from '@/components/theme/theme-constants';
import { Footer } from '@/components/footer';
import { NavigationLink } from '@/components/navigation-link';
import { HOME_PAGE_CONTENT } from './home-page-content';
import { ProductCard } from './components/product-card';
import blurredBackgroundImg from './icons/blurred-background.png';

const StyledCard = styled(Card)({
  borderRadius: '12px',
  background: `linear-gradient(0deg, rgba(0, 0, 0, 0.33) 0%, rgba(0, 0, 0, 0.33) 100%), url(${blurredBackgroundImg.src})`,
  backgroundSize: 'cover',
  outline: '1px solid rgba(199, 181, 255, 0.24)',
  outlineOffset: '-1px',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
});

export const HomePage = () => {
  const t = useTranslations();
  const { zoom } = React.useContext(ThemeContext);

  return (
    <Box display="flex" justifyContent="center">
      <Stack
        sx={(theme) => ({
          width: '100%',
          margin: theme.spacing(4),
          zoom: themeConstants.zoom[zoom],
          [theme.breakpoints.up('sm')]: {
            marginTop: theme.spacing(6),
            marginX: 17,
          },
          [theme.breakpoints.up('lg')]: {
            maxWidth: '1200px',
          },
        })}
      >
        <Stack gap={3}>
          <Typography variant="h2" fontSize="32px" lineHeight="40px" letterSpacing="-0.01rem">
            {t('home-page:title')}
          </Typography>
          <Typography fontSize="16px" lineHeight="24px">
            {t('home-page:description')}
          </Typography>
          {HOME_PAGE_CONTENT.map((section, index) => {
            const isUGSSection = section.titleKey === 'home-page:unity-gaming-services:title';
            const isUnityGrowSection = section.titleKey === 'home-page:unity-grow:title';

            return (
              <React.Fragment key={index}>
                <Divider />
                <Stack gap={3} marginBottom={3}>
                  <Typography variant="h3" fontSize="24px">
                    {t(section.titleKey)}
                  </Typography>
                  <Typography color="textSecondary">{t(section.description)}</Typography>
                  <Grid container spacing={3}>
                    {isUGSSection ? (
                      <>
                        <Grid size={12}>
                          <div className="dark">
                            <StyledCard>
                              <CardContent>
                                <Stack
                                  flexDirection={{ xs: 'column', md: 'row' }}
                                  alignItems={{ xs: 'flex-start', md: 'center' }}
                                  justifyContent="space-between"
                                  gap={3}
                                >
                                  <Box flexGrow={1}>
                                    <Stack flexDirection="row" gap={1} marginBottom={1.5}>
                                      <Box display="flex" alignItems="center" color="#6C62FF" paddingRight={0.5}>
                                        <SparkleIcon size={20} aria-hidden="true" focusable="false" />
                                      </Box>
                                      <Typography variant="h3" fontSize={18}>
                                        {t('home-page:unity-gaming-services:banner:title')}
                                      </Typography>
                                    </Stack>
                                    <Typography color="textSecondary">
                                      {t('home-page:unity-gaming-services:banner:description')}
                                    </Typography>
                                  </Box>
                                  <Stack flexDirection="row" gap={2}>
                                    <Button
                                      variant="contained"
                                      color="info"
                                      endIcon={<ArrowRightIcon />}
                                      component={NavigationLink}
                                      href="https://docs.unity.com/services/getting-started"
                                      sx={{ '&:hover': { color: 'inherit' } }}
                                    >
                                      {t('home-page:unity-gaming-services:banner:cta1')}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="info"
                                      endIcon={<ArrowRightIcon />}
                                      component={NavigationLink}
                                      href="/cloud"
                                      sx={({ vars }) => ({
                                        color: vars.palette.text.primary,
                                        '&:hover': { color: 'inherit' },
                                      })}
                                    >
                                      {t('home-page:unity-gaming-services:banner:cta3')}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="info"
                                      endIcon={<ArrowRightIcon />}
                                      component={NavigationLink}
                                      href="https://docs.unity.com/ugs-use-cases"
                                      sx={({ vars }) => ({
                                        color: vars.palette.text.primary,
                                        borderColor: vars.palette.background.paperElevation3,
                                        '&:hover': { color: 'inherit' },
                                      })}
                                    >
                                      {t('home-page:unity-gaming-services:banner:cta2')}
                                    </Button>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </StyledCard>
                          </div>
                        </Grid>
                        <Grid container size={{ xs: 12, md: 6 }} spacing={3}>
                          {section.cards.slice(0, 2).map((card, index) => (
                            <Grid key={index} size={12}>
                              <ProductCard
                                title={t(card.titleKey)}
                                description={t(card.description)}
                                href={card.href}
                                icon={card.icon}
                                iconColor={card.iconColor}
                                displayLinksInColumns={card.displayLinksInColumns}
                                linkSections={card.linkSections}
                              />
                            </Grid>
                          ))}
                        </Grid>
                        <Grid container size={{ xs: 12, md: 6 }} spacing={3}>
                          {section.cards.slice(2).map((card, index) => (
                            <Grid key={index} size={12}>
                              <ProductCard
                                title={t(card.titleKey)}
                                description={t(card.description)}
                                href={card.href}
                                icon={card.icon}
                                iconColor={card.iconColor}
                                displayLinksInColumns={card.displayLinksInColumns}
                                linkSections={card.linkSections}
                              />
                            </Grid>
                          ))}
                        </Grid>
                        <Grid size={12}>
                          <Card>
                            <CardContent>
                              <Stack
                                flexDirection={{ xs: 'column', md: 'row' }}
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                justifyContent="space-between"
                                gap={{ xs: 2, md: 4 }}
                              >
                                <Box flexGrow={1}>
                                  <Stack flexDirection="row" gap={1} marginBottom={1.5}>
                                    <Box display="flex" alignItems="center" color="action.active" paddingRight={0.5}>
                                      <CodeIcon size={20} aria-hidden="true" focusable="false" />
                                    </Box>
                                    <Typography variant="h3" fontSize={18}>
                                      {t('home-page:unity-gaming-services:card-8:title')}
                                    </Typography>
                                  </Stack>
                                  <Typography color="textSecondary">
                                    {t('home-page:unity-gaming-services:card-8:description')}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Button
                                    variant="outlined"
                                    href="https://services.docs.unity.com/"
                                    aria-label={t('link:generic:aria-label', {
                                      title: t('mdx:video-embed:learn-more'),
                                    })}
                                    endIcon={<ArrowRightIcon />}
                                    sx={({ vars }) => ({
                                      color: vars.palette.text.primary,
                                      borderColor: vars.palette.background.paperElevation3,
                                      width: 'max-content',
                                    })}
                                  >
                                    {t('mdx:video-embed:learn-more')}
                                  </Button>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      </>
                    ) : isUnityGrowSection ? (
                      <>
                        <Grid container size={{ xs: 12, md: 6 }} spacing={3}>
                          {section.cards.slice(0, 4).map((card, index) => (
                            <Grid key={index} size={12}>
                              <ProductCard
                                title={t(card.titleKey)}
                                description={t(card.description)}
                                href={card.href}
                                icon={card.icon}
                                sx={{
                                  '& .cardDescription': {
                                    minHeight: '2.5rem',
                                  },
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                        <Grid container size={{ xs: 12, md: 6 }} spacing={3}>
                          {section.cards.slice(4).map((card, index) => (
                            <Grid key={index} size={12}>
                              <ProductCard
                                title={t(card.titleKey)}
                                description={t(card.description)}
                                href={card.href}
                                icon={card.icon}
                                sx={{
                                  '& .cardDescription': {
                                    minHeight: '2.5rem',
                                  },
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    ) : (
                      section.cards.map((card, index) => (
                        <Grid key={index} size={{ xs: 12, md: 6 }}>
                          <ProductCard
                            title={t(card.titleKey)}
                            description={t(card.description)}
                            href={card.href}
                            icon={card.icon}
                            iconColor={card.iconColor}
                            displayLinksInColumns={card.displayLinksInColumns}
                            linkSections={card.linkSections}
                          />
                        </Grid>
                      ))
                    )}
                  </Grid>
                </Stack>
              </React.Fragment>
            );
          })}
        </Stack>
        <Divider sx={{ marginTop: 2 }} />
        <Footer />
      </Stack>
    </Box>
  );
};
